// Both attack lanes plan the same pre-strike approach with the shared scene/occupancy readers.
// Ability casts land their cloned caster in the verdict; explicit attacks carry the landing in
// their existing reaction transport. Unsupported movement is refused before any real draw.
import { type JanusCtx, type Row, handBack, record, scalar } from '../context.js';
import { resolveWalkToward, type WalkTowardResult } from '../../movement/walk-toward.js';
import { encounterShapeOf, forcedMovementReaders, landRow } from './check-push-and-pull-after-hit.js';
import { type RestShape } from '../../inventory/floor-move.js';
import { discernVariableTags } from '../discern-variable-tags.js';
import { attackGrantsTeleportChoice } from './ability-teleport.js';

const LAYER = 'caster_moves_towards_target_in_ft';

/** The move distance PHP reads: the resolved item's layer, else the nested attack_ability_arr rider's; null when neither is authored. */
function moveFeet(ctx: JanusCtx, ability: Row, caster: Row): number | null {
  let raw = ctx.read.value(ability, LAYER);
  if (raw == null || raw === '') {
    const rider = record(ability.attack_ability_arr);
    if (rider) raw = ctx.read.value(rider, LAYER);
  }
  if (raw == null || raw === '') return null;
  const text = String(scalar(raw)).trim();
  return /^[+-]?\d+(\.\d+)?$/.test(text) ? Number(text)
    : discernVariableTags(ctx, text, caster, { reason: 'move_caster_moves_towards_target_in_ft' });
}

export function hasMoveTowardsTarget(ctx: { read: Pick<JanusCtx['read'], 'has'> }, ability: Row): boolean {
  return ctx.read.has(ability, LAYER) || (record(ability.attack_ability_arr) !== null && ctx.read.has(record(ability.attack_ability_arr), LAYER));
}

/** Shared pre-draw approach plan. Both attack lanes use the ability frame's geometry readers. */
export function planCasterTowardsTarget(ctx: JanusCtx, ability: Row, caster: Row, targets: Row[], selected: number): WalkTowardResult {
  if (attackGrantsTeleportChoice(ability, ctx.read)) return { kind: 'none', why: 'no-distance' };
  const ft = moveFeet(ctx, ability, caster);
  if (ft === null || ft <= 0) return { kind: 'none', why: 'no-distance' };
  if (ctx.read.has(ability, 'caster_dive_bombs_target') || ctx.read.has(record(ability.attack_ability_arr) ?? {}, 'caster_dive_bombs_target'))
    handBack('move_caster_moves_towards_target_in_ft:dive-bomb');
  if (selected !== 1 || targets.length !== 1) handBack('move_caster_moves_towards_target_in_ft:target-count');
  const shape = encounterShapeOf(ctx);
  const plan = resolveWalkToward({ encounter: ctx.encounter, shape: shape as unknown as RestShape | null, mover: caster, target: targets[0]!, moveFt: ft },
    forcedMovementReaders(ctx));
  if (plan.kind === 'handback') handBack('move_caster_moves_towards_target_in_ft:' + plan.reason);
  return plan;
}

export function moveCasterTowardsTarget(ctx: JanusCtx, ability: Row, caster: Row, targets: Row[], selected: number): void {
  const plan = planCasterTowardsTarget(ctx, ability, caster, targets, selected);
  if (plan.kind === 'move') landRow(ctx, caster, plan.end);
}
